import React, { useMemo, useState } from 'react';
import './EligibilityCheck.css';
import {
  computeEligibilityGoNoGo,
  ELIGIBILITY_CARD_STATUS_LABELS,
  ELIGIBILITY_SECTION_INTRO,
  ELIGIBILITY_SUMMARY_AI_REPORT_NUDGE,
} from '../utils/eligibilityGoNoGo';
import { YesNoUnknownValues } from '../types/feasibility.js';

const CARD_ICONS = {
  age: 'fi-rr-calendar',
  plan: 'fi-rr-map',
  conservation: 'fi-rr-clipboard',
  land_use: 'fi-rr-home-location'
};

const OPENING_SUMMARY =
  'נבדקים ארבעה מבחנים אוטומטיים על בסיס דו״ח הנכס. כשחסר נתון קריטי הסולם מוגבל לרמה בינונית ומטה עד להשלמת הנתון.';

const COPY_LIBRARY = {
  age: {
    pass: 'גיל המבנה תומך בהתכנות להתחדשות עירונית. לפי הנתונים שנמצאו, שנת הבנייה היא {year_built} (או גיל המבנה המשוער {building_age}).',
    fail: 'גיל המבנה מגביל את ההתכנות להתחדשות עירונית. לפי הנתונים שנמצאו, שנת הבנייה היא {year_built} (או גיל המבנה המשוער {building_age}), כלומר המבנה אינו לפני 1980.',
    unknown: 'חסרים נתוני גיל או שנת בנייה.'
  },
  plan: {
    pass: 'נמצאה אינדיקציה לתכנית/מדיניות התחדשות עירונית רלוונטית באזור הנכס{plan_name_or_id}. ממצא זה תומך בהתכנות לקידום פרויקט.',
    fail: 'לא נמצאה תכנית/מדיניות התחדשות עירונית רלוונטית בהתבסס על המידע שנאסף. במקרים מסוימים היעדר תכנית עשוי להגביל את ההתכנות לקידום פרויקט.',
    fail_7778: 'נמצאה תכנית התחדשות עירונית באזור הנכס{plan_name_or_id}, אך זוהתה גם תכנית לפי סעיף 77/78{section7778_plan_id} המקפיאה מתן היתרי בנייה. הקפאה זו מגבילה את האפשרות לקדם פרויקט בשלב זה.',
    unknown: 'חסר מידע על תכנית/אזור התחדשות עירונית.'
  },
  conservation: {
    pass: 'לא נמצאה אינדיקציה לכך שהמבנה מיועד לשימור (או נמצא כי אינו לשימור). ממצא זה תומך בהתכנות לקידום פרויקט.',
    fail: 'נמצאה אינדיקציה לכך שהמבנה מיועד לשימור. ייעוד לשימור עשוי להגביל או לדרוש התאמות משמעותיות בקידום פרויקט התחדשות.',
    unknown: 'חסר מידע על שימור המבנה.'
  },
  land_use: {
    pass: 'ייעוד הקרקע שנמצא הוא למגורים {land_use}. ממצא זה תומך בהתכנות להתחדשות עירונית.',
    pass_mixed: 'ייעוד הקרקע בתב"ע אינו למגורים {land_use}, אך המבנה עצמו רשום כמבנה מגורים {building_use} — מאפיין של מבנה מעורב (למשל מסחר בקומת הקרקע ומגורים מעל). ממצא זה תומך בהתכנות להתחדשות עירונית.',
    fail: 'ייעוד הקרקע שנמצא אינו למגורים {land_use}. ממצא זה מגביל את ההתכנות להתחדשות עירונית במסלול מגורים.',
    unknown: 'חסר מידע על ייעוד הקרקע.'
  }
};

const replacePlaceholders = (text, values) => {
  let result = text;
  Object.entries(values).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    result = result.replaceAll(placeholder, value || '');
  });
  return result
    .replace(/\s+\(\s*\)/g, '')
    .replace(/\s+{[^}]+}\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
};

/** Unknown: single short line (missingImpact). Else: criterion copy. */
const getEligibilityCardDescription = (test) => {
  if (test.status === 'unknown' && test.missingImpact) {
    return test.missingImpact;
  }
  const copyKey = test.copyKey.replace(`${test.id}_`, '');
  const copyTemplate = COPY_LIBRARY[test.id][copyKey] || COPY_LIBRARY[test.id][test.status];
  return replacePlaceholders(copyTemplate, test.evidence || {});
};

const MIN_CONSTRUCTION_YEAR = 1900;

/** @returns {{ ok: boolean, year?: number, reason?: string }} */
function getConstructionYearValidity(raw, maxYear) {
  const t = String(raw ?? '').trim();
  if (!t) return { ok: false, reason: 'empty' };
  if (!/^\d{3,4}$/.test(t)) return { ok: false, reason: 'digits' };
  const y = parseInt(t, 10);
  if (!Number.isFinite(y)) return { ok: false, reason: 'invalid' };
  if (y < MIN_CONSTRUCTION_YEAR) return { ok: false, reason: 'min' };
  if (y > maxYear) return { ok: false, reason: 'max' };
  return { ok: true, year: y };
}

const YEAR_ERROR_MESSAGES = {
  digits: 'נא להזין שנה מספרית (3–4 ספרות), ללא עשרוניים.',
  invalid: 'שנה לא תקינה.',
  min: `שנת הבנייה חייבת להיות ${MIN_CONSTRUCTION_YEAR} לפחות.`,
  max: 'לא ניתן להזין שנה עתידית.',
};

function EligibilityUnknownOverrideForm({ test, onApply, busy }) {
  const [year, setYear] = useState('');
  const [yearError, setYearError] = useState('');
  const [plan, setPlan] = useState('');
  const [land, setLand] = useState('');

  if (typeof onApply !== 'function') return null;

  const disabled = !!busy;
  const maxYear = new Date().getFullYear();
  const yearValidity = getConstructionYearValidity(year, maxYear);
  const ageCanSubmit = yearValidity.ok;

  if (test.id === 'age') {
    return (
      <div className="eligibility-override-stack">
        <label className="eligibility-override-label" htmlFor={`elig-age-${test.id}`}>שנת בנייה (אם ידועה)</label>
        <div className="eligibility-override-field">
          <input
            id={`elig-age-${test.id}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            className={`eligibility-override-input${yearError ? ' eligibility-override-input--error' : ''}`}
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              setYearError('');
            }}
            onBlur={() => {
              const v = getConstructionYearValidity(year, maxYear);
              if (!String(year).trim()) {
                setYearError('');
                return;
              }
              if (!v.ok && v.reason && v.reason !== 'empty') {
                setYearError(YEAR_ERROR_MESSAGES[v.reason] || YEAR_ERROR_MESSAGES.invalid);
              } else {
                setYearError('');
              }
            }}
            disabled={disabled}
            placeholder="למשל 1975"
            aria-invalid={!!yearError}
            aria-describedby={[`elig-age-help-${test.id}`, yearError ? `elig-age-err-${test.id}` : null]
              .filter(Boolean)
              .join(' ')}
          />
          <p id={`elig-age-help-${test.id}`} className="eligibility-override-help">
            {`שנה מלאה בין ${MIN_CONSTRUCTION_YEAR} ל־${maxYear}.`}
          </p>
          {yearError ? (
            <p id={`elig-age-err-${test.id}`} className="eligibility-override-error">
              {yearError}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="eligibility-override-btn btn btn-primary btn-sm"
          disabled={disabled || !ageCanSubmit}
          onClick={() => {
            const v = getConstructionYearValidity(year, maxYear);
            if (!v.ok || !v.year) {
              if (v.reason && v.reason !== 'empty') {
                setYearError(YEAR_ERROR_MESSAGES[v.reason] || YEAR_ERROR_MESSAGES.invalid);
              }
              return;
            }
            onApply({
              constructionYear: v.year,
              constructionYearConfidence: 'high',
              pre1980Eligibility: v.year < 1980 ? YesNoUnknownValues.YES : YesNoUnknownValues.NO,
            });
          }}
        >
          עדכון חישוב
        </button>
      </div>
    );
  }

  if (test.id === 'plan') {
    return (
      <div className="eligibility-override-stack">
        <label className="eligibility-override-label" htmlFor={`elig-plan-${test.id}`}>
          האם יש אינדיקציה לאזור התחדשות / פינוי־בינוי?
        </label>
        <div className="eligibility-override-field">
          <select
            id={`elig-plan-${test.id}`}
            name={`elig-plan-${test.id}`}
            autoComplete="off"
            className="eligibility-override-select"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            disabled={disabled}
            aria-describedby={`elig-plan-help-${test.id}`}
          >
            <option value="">בחרו…</option>
            <option value={YesNoUnknownValues.YES}>כן</option>
            <option value={YesNoUnknownValues.NO}>לא</option>
            <option value={YesNoUnknownValues.UNKNOWN}>לא יודעים / לא בטוחים</option>
          </select>
          <p id={`elig-plan-help-${test.id}`} className="eligibility-override-help">
            יש לבחור ערך מהרשימה לפני עדכון החישוב.
          </p>
        </div>
        <button
          type="button"
          className="eligibility-override-btn btn btn-primary btn-sm"
          disabled={disabled || !plan}
          onClick={() => onApply({ declaredPinuyBinuyArea: plan })}
        >
          עדכון חישוב
        </button>
      </div>
    );
  }

  if (test.id === 'conservation') {
    return (
      <div className="eligibility-override-stack">
        <span className="eligibility-override-label">סטטוס שימור לפי ידיעתכם</span>
        <div className="eligibility-override-row eligibility-override-row--pair">
          <button
            type="button"
            className="eligibility-override-btn btn btn-outline btn-sm"
            disabled={disabled}
            onClick={() =>
              onApply({
                conservationListed: false,
                buildingConservationStatus: { hasConservation: false, checked: true, source: 'user' },
              })
            }
          >
            לא מסווג לשימור
          </button>
          <button
            type="button"
            className="eligibility-override-btn btn btn-outline btn-sm"
            disabled={disabled}
            onClick={() =>
              onApply({
                conservationListed: true,
                buildingConservationStatus: { hasConservation: true, checked: true, source: 'user' },
              })
            }
          >
            מסווג לשימור
          </button>
        </div>
      </div>
    );
  }

  if (test.id === 'land_use') {
    return (
      <div className="eligibility-override-stack">
        <label className="eligibility-override-label" htmlFor={`elig-land-${test.id}`}>
          ייעוד קרקע מהתכנית (טקסט קצר)
        </label>
        <div className="eligibility-override-field">
          <input
            id={`elig-land-${test.id}`}
            name={`elig-land-${test.id}`}
            type="text"
            autoComplete="off"
            className="eligibility-override-input"
            value={land}
            onChange={(e) => setLand(e.target.value)}
            disabled={disabled}
            placeholder="למשל: מגורים א"
            aria-describedby={`elig-land-help-${test.id}`}
          />
          <p id={`elig-land-help-${test.id}`} className="eligibility-override-help">
            הזינו ניסוח קצר כפי שמופיע בתכנית.
          </p>
        </div>
        <button
          type="button"
          className="eligibility-override-btn btn btn-primary btn-sm"
          disabled={disabled || !land.trim()}
          onClick={() => onApply({ landUse: land.trim() })}
        >
          עדכון חישוב
        </button>
      </div>
    );
  }

  return null;
}

const EligibilityCheck = ({ feasibilityData, onApplyFeasibilityOverride, feasibilityOverrideBusy = false }) => {
  // Which resolved (pass/fail) criteria have their manual-override form revealed.
  const [openOverrides, setOpenOverrides] = useState({});
  const toggleOverride = (id) => setOpenOverrides((o) => ({ ...o, [id]: !o[id] }));

  const { tests, normalizedScore, summaryCopy } = useMemo(() => {
    const { tests: testsList, normalizedScore: normalized } = computeEligibilityGoNoGo(feasibilityData);

    const summaryParagraphs = testsList.map((test) => getEligibilityCardDescription(test));

    return {
      tests: testsList,
      normalizedScore: normalized,
      summaryCopy: summaryParagraphs,
    };
  }, [feasibilityData]);


  // scaleIndex 0..4: 4 = best eligibility, 0 = worst.
  const scaleIndex = Math.min(4, Math.max(0, normalizedScore + 2));
  // Design scale is LTR green(left)→red(right): best sits on the left. Each of
  // the 5 legend labels sits centered in an equal cell, so put the marker at the
  // active cell's center — the dot then lines up exactly under its label.
  const markerLeft = (((4 - scaleIndex) + 0.5) / 5) * 100;
  const LEVELS = ['נמוכה מאוד', 'נמוכה', 'בינונית', 'גבוהה', 'גבוהה מאוד']; // by scaleIndex 0..4
  const LEVEL_COLORS = ['#dc2626', '#f59e0b', '#667085', '#10b981', '#10b981'];
  const levelLabel = LEVELS[scaleIndex];
  const levelColor = LEVEL_COLORS[scaleIndex];

  const unknownTests = tests.filter((t) => t.status === 'unknown');
  const missingCount = unknownTests.length;
  const missingNames = unknownTests.map((t) => t.title).join(' ו');

  const STATUS_STYLE = {
    pass: { bar: '#10b981', bg: 'rgba(16,185,129,.12)', color: '#047857', dot: '#10b981' },
    fail: { bar: '#dc2626', bg: 'rgba(220,38,38,.1)', color: '#dc2626', dot: '#dc2626' },
    unknown: { bar: '#f59e0b', bg: 'rgba(245,158,11,.13)', color: '#b45309', dot: '#d97706' },
  };

  return (
    <div className="eligc">
      {/* Header + disclaimer */}
      <div className="eligc-card eligc-headcard">
        <h2 className="eligc-title">בדיקת זכאות להתחדשות עירונית</h2>
        <p className="eligc-disclaimer">{ELIGIBILITY_SECTION_INTRO}</p>
      </div>

      {/* Verdict banner: estimated level + missing-data note + scale */}
      <div className="eligc-card eligc-verdict">
        <div className="eligc-verdict-top">
          <div className="eligc-verdict-level">
            <span className="eligc-verdict-label">רמת זכאות משוערת</span>
            <span className="eligc-verdict-value">
              <span className="eligc-verdict-dot" style={{ background: levelColor }} />
              {levelLabel}
            </span>
          </div>
          {missingCount > 0 && (
            <div className="eligc-verdict-warn">
              <i className="fi fi-rr-triangle-warning" />
              <span>
                <b>{missingCount} מתוך 4 מבחנים קריטיים חסרים נתונים.</b>{' '}
                השלמת {missingNames} עשויה להעלות את הדירוג מהותית.
              </span>
            </div>
          )}
        </div>
        <div className="eligc-scale">
          <div className="eligc-scale-bar">
            <span className="eligc-scale-marker" style={{ left: `${markerLeft}%` }} />
          </div>
          <div className="eligc-scale-legend">
            {['גבוהה מאוד', 'גבוהה', 'בינונית', 'נמוכה', 'נמוכה מאוד'].map((lbl, i) => (
              <span key={lbl} className={(4 - i) === scaleIndex ? 'active' : ''}>{lbl}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Criteria cards */}
      <div className="eligc-section-label">פירוט המבחנים</div>
      <div className={`eligc-cards${feasibilityOverrideBusy ? ' eligc-cards--busy' : ''}`}>
        {tests.map((test) => {
          const s = STATUS_STYLE[test.status] || STATUS_STYLE.unknown;
          return (
            <div key={test.id} className="eligc-crit">
              <div className="eligc-crit-bar" style={{ background: s.bar }} />
              <div className="eligc-crit-body">
                <div className="eligc-crit-head">
                  <span className="eligc-crit-icon" style={{ background: s.bg, color: s.color }}>
                    <i className={`fi ${CARD_ICONS[test.id] || 'fi-rr-clipboard'}`} />
                  </span>
                  <span className="eligc-crit-name">{test.title}</span>
                </div>
                <span className="eligc-crit-status" style={{ background: s.bg, color: s.color }}>
                  <span className="eligc-crit-status-dot" style={{ background: s.dot }} />
                  {ELIGIBILITY_CARD_STATUS_LABELS[test.status]}
                </span>
                <p className="eligc-crit-desc">{getEligibilityCardDescription(test)}</p>
                {typeof onApplyFeasibilityOverride === 'function' && test.status !== 'unknown' && (
                  <button
                    type="button"
                    className="eligc-crit-edit"
                    onClick={() => toggleOverride(test.id)}
                    aria-expanded={!!openOverrides[test.id]}
                  >
                    <i className={`fi ${openOverrides[test.id] ? 'fi-rr-cross-small' : 'fi-rr-pencil'}`} />
                    <span className="eligc-crit-edit-label">{openOverrides[test.id] ? 'סגירה' : 'עדכון נתון ידני'}</span>
                  </button>
                )}
                {(test.status === 'unknown' || openOverrides[test.id]) && (
                  <div className="eligc-crit-input">
                    <EligibilityUnknownOverrideForm
                      test={test}
                      onApply={onApplyFeasibilityOverride}
                      busy={feasibilityOverrideBusy}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary findings */}
      <div className="eligc-card eligc-summary">
        <h3 className="eligc-summary-title">סיכום הבדיקה</h3>
        <p className="eligc-summary-opening">{OPENING_SUMMARY}</p>
        <div className="eligc-findings">
          {tests.map((test, idx) => (
            <div key={test.id} className="eligc-finding">
              <span className={`eligc-finding-ic eligc-finding-ic--${test.status === 'pass' ? 'pass' : 'warn'}`}>
                <i className={`fi ${test.status === 'pass' ? 'fi-rr-check' : 'fi-rr-exclamation'}`} />
              </span>
              <span className="eligc-finding-text">{summaryCopy[idx]}</span>
            </div>
          ))}
        </div>
        <div className="eligc-summary-nudge">
          <i className="fi fi-rr-info" />
          <span>{ELIGIBILITY_SUMMARY_AI_REPORT_NUDGE}</span>
        </div>
      </div>
    </div>
  );
};

export default EligibilityCheck;
